/**
 * Regional team targets vs achieved: one horizontal bar per billing-region
 * team, plus a combined row. Hand-built CSS bars (EMERGE has no chart library).
 *
 * Geometry per row: the track is the TARGET (100%), the fill is ACHIEVED and
 * the tick is EXPECTED-TO-DATE (target × elapsed share of the cycle). Every
 * figure is USD; the server has already translated non-USD targets and
 * actuals, so summing across rows here is a same-currency sum.
 *
 * ⚠️ Money stays a decimal string. Bar widths are the only numbers, via
 * `pctWidth` on percentage strings computed with BigInt.
 *
 * ⚠️ A NULL target is "no target", not zero: the row prints `—` and draws no
 * bar. The combined target is null as soon as any team's target is null,
 * because a partial sum would look like the whole.
 *
 * Accessibility: the visual block is `role="img"` with an aria-label that
 * summarises every row; the same figures are in a visually hidden table.
 */
import { EmptyState } from '@/components/ui';
import { formatMoneyText } from '@/components/ui/Money';
import { CompactMoney } from '@/components/dashboards/TargetTiles';
import { cx } from '@/lib/cx';
import { formatDateOnly } from '@/lib/format';
import {
  achievedTone,
  compareDecimal,
  formatPct,
  isDecimalString,
  pctWidth,
  subtractDecimal,
  sumUsd,
  type SalesTargetTone,
} from '@/lib/sales-targets';
import type { SalesTargetRegion } from '@/lib/types';

const CURRENCY = 'USD';

const FILL_CLASS: Record<SalesTargetTone, string> = {
  success: 'bg-success',
  neutral: 'bg-fg-secondary',
  warning: 'bg-warning',
  danger: 'bg-danger',
};

const TEXT_CLASS: Record<SalesTargetTone, string> = {
  success: 'text-success',
  neutral: 'text-fg',
  warning: 'text-warning',
  danger: 'text-danger',
};

interface ChartRow {
  key: string;
  label: string;
  targetUsd: string | null;
  achievedUsd: string | null;
  expectedToDateUsd: string | null;
  gapUsd: string | null;
  /** Achieved ÷ target as a percentage string (unclamped), null = not measurable. */
  achievedPct: string | null;
  /** Expected-to-date ÷ target as a percentage string, for the tick only. */
  expectedPct: string | null;
  tone: SalesTargetTone;
  isTotal: boolean;
}

/** A decimal string as integer cents (extra decimals truncated), else null. */
function toCents(value: string | null): bigint | null {
  if (!isDecimalString(value)) return null;
  const trimmed = value.trim();
  const negative = trimmed.startsWith('-');
  const unsigned = negative ? trimmed.slice(1) : trimmed;
  const [whole = '0', fraction = ''] = unsigned.split('.');
  const cents = BigInt(whole) * 100n + BigInt((fraction + '00').slice(0, 2));
  return negative ? -cents : cents;
}

/** part ÷ whole × 100 to one decimal, exact (BigInt), as a decimal string. */
function percentOf(part: string | null, whole: string | null): string | null {
  const p = toCents(part);
  const w = toCents(whole);
  if (p === null || w === null || w <= 0n) return null;
  const scaled = p * 2000n;
  const tenths = (scaled < 0n ? scaled - w : scaled + w) / (2n * w);
  const negative = tenths < 0n;
  const abs = negative ? -tenths : tenths;
  return `${negative ? '-' : ''}${abs / 10n}.${abs % 10n}`;
}

function gapText(gapUsd: string | null): string {
  const sign = compareDecimal(gapUsd, '0');
  if (sign === null) return 'Gap —';
  if (sign > 0) return `${formatMoneyText(gapUsd, CURRENCY)} to go`;
  if (sign < 0) return `${formatMoneyText(subtractDecimal('0', gapUsd), CURRENCY)} over target`;
  return 'On target';
}

function toRow(
  key: string,
  label: string,
  figures: Pick<ChartRow, 'targetUsd' | 'achievedUsd' | 'expectedToDateUsd' | 'gapUsd'> & {
    achievedPct: string | null;
  },
  isTotal: boolean,
): ChartRow {
  const achievedPct = figures.achievedPct ?? percentOf(figures.achievedUsd, figures.targetUsd);
  return {
    key,
    label,
    targetUsd: figures.targetUsd,
    achievedUsd: figures.achievedUsd,
    expectedToDateUsd: figures.expectedToDateUsd,
    gapUsd: figures.gapUsd,
    achievedPct,
    expectedPct: percentOf(figures.expectedToDateUsd, figures.targetUsd),
    tone: achievedTone({
      achievedUsd: figures.achievedUsd,
      targetRevenueUsd: figures.targetUsd,
      achievedPct,
      paceGapUsd: subtractDecimal(figures.expectedToDateUsd, figures.achievedUsd),
    }),
    isTotal,
  };
}

function buildRows(regions: SalesTargetRegion[]): ChartRow[] {
  const rows = regions.map((region) =>
    toRow(
      region.key,
      region.label,
      {
        targetUsd: region.targetUsd,
        achievedUsd: region.achievedUsd,
        expectedToDateUsd: region.expectedToDateUsd,
        gapUsd: region.gapUsd,
        achievedPct: region.achievedPct,
      },
      false,
    ),
  );

  if (regions.length > 1) {
    const anyTargetMissing = regions.some((region) => !isDecimalString(region.targetUsd));
    const targetUsd = anyTargetMissing ? null : sumUsd(regions.map((r) => r.targetUsd));
    const achievedUsd = sumUsd(regions.map((r) => r.achievedUsd));
    const expectedToDateUsd = anyTargetMissing ? null : sumUsd(regions.map((r) => r.expectedToDateUsd));
    rows.push(
      toRow(
        '__total__',
        'All teams',
        {
          targetUsd,
          achievedUsd,
          expectedToDateUsd,
          gapUsd: subtractDecimal(targetUsd, achievedUsd ?? '0'),
          achievedPct: null,
        },
        true,
      ),
    );
  }
  return rows;
}

function rowSummary(row: ChartRow): string {
  if (!isDecimalString(row.targetUsd)) {
    return `${row.label}: no target, achieved ${formatMoneyText(row.achievedUsd, CURRENCY)}`;
  }
  return (
    `${row.label}: achieved ${formatMoneyText(row.achievedUsd, CURRENCY)} of ` +
    `${formatMoneyText(row.targetUsd, CURRENCY)} (${formatPct(row.achievedPct)}), ` +
    `expected to date ${formatMoneyText(row.expectedToDateUsd, CURRENCY)}, ${gapText(row.gapUsd)}`
  );
}

function RegionBar({ row }: { row: ChartRow }) {
  const hasTarget = isDecimalString(row.targetUsd);

  return (
    <li className={cx('flex flex-col gap-1.5 py-2.5', row.isTotal && 'border-t border-border pt-3')}>
      <div className="flex min-w-0 items-baseline justify-between gap-3">
        <span className={cx('min-w-0 truncate text-sm text-fg', row.isTotal ? 'font-semibold' : 'font-medium')}>
          {row.label}
        </span>
        <span
          className={cx(
            'shrink-0 text-sm font-semibold tabular-nums',
            hasTarget ? TEXT_CLASS[row.tone] : 'text-fg-subtle',
          )}
        >
          {hasTarget ? formatPct(row.achievedPct) : '—'}
        </span>
      </div>

      {hasTarget ? (
        <div className="relative h-3">
          <div className="absolute inset-0 overflow-hidden rounded-xs bg-surface-sunken">
            <div
              className={cx('h-full rounded-xs', FILL_CLASS[row.tone])}
              style={{ width: `${pctWidth(row.achievedPct)}%` }}
            />
          </div>
          {row.expectedPct !== null && (
            <div
              className="absolute -top-1 -bottom-1 w-0.5 -translate-x-1/2 rounded-xs bg-fg"
              style={{ left: `${pctWidth(row.expectedPct)}%` }}
            />
          )}
        </div>
      ) : null}

      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 text-xs text-fg-muted">
        <span className="min-w-0">
          {hasTarget ? (
            <>
              Achieved <CompactMoney amount={row.achievedUsd} currency={CURRENCY} className="text-fg" /> of{' '}
              <CompactMoney amount={row.targetUsd} currency={CURRENCY} className="text-fg" /> (
              {formatPct(row.achievedPct)})
            </>
          ) : (
            <>
              Achieved <CompactMoney amount={row.achievedUsd} currency={CURRENCY} className="text-fg" /> · Target —
            </>
          )}
        </span>
        {hasTarget && (
          <span className="tabular-nums">
            Expected <CompactMoney amount={row.expectedToDateUsd} currency={CURRENCY} /> · {gapText(row.gapUsd)}
          </span>
        )}
      </div>
    </li>
  );
}

export function RegionalTargetsChart({ regions, asOf }: { regions: SalesTargetRegion[]; asOf: string }) {
  if (regions.length === 0) {
    return (
      <EmptyState
        title="No regional team targets for this cycle"
        description="Set invoiced targets for the regional teams on the Catalog page."
      />
    );
  }

  const rows = buildRows(regions);
  const asOfText = formatDateOnly(asOf);
  const label = `Regional team targets in USD as of ${asOfText}. ${rows.map(rowSummary).join('. ')}.`;

  return (
    <div className="min-w-0">
      <div role="img" aria-label={label}>
        <ul aria-hidden="true" className="m-0 flex list-none flex-col p-0">
          {rows.map((row) => (
            <RegionBar key={row.key} row={row} />
          ))}
        </ul>
        <div aria-hidden="true" className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-2xs text-fg-muted">
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block h-2 w-3 rounded-xs bg-surface-sunken" /> Target
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block h-2 w-3 rounded-xs bg-fg-secondary" /> Achieved
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block h-3 w-0.5 rounded-xs bg-fg" /> Expected to date
          </span>
          <span>As of {asOfText}</span>
        </div>
      </div>

      <table className="sr-only">
        <caption>Regional team targets in USD as of {asOfText}</caption>
        <thead>
          <tr>
            <th scope="col">Team</th>
            <th scope="col">Target</th>
            <th scope="col">Achieved</th>
            <th scope="col">% achieved</th>
            <th scope="col">Expected to date</th>
            <th scope="col">Gap</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key}>
              <th scope="row">{row.label}</th>
              <td>{formatMoneyText(row.targetUsd, CURRENCY)}</td>
              <td>{formatMoneyText(row.achievedUsd, CURRENCY)}</td>
              <td>{formatPct(row.achievedPct)}</td>
              <td>{formatMoneyText(row.expectedToDateUsd, CURRENCY)}</td>
              <td>{gapText(row.gapUsd)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
